import type { KeyboardEvent } from 'react';
import { CheckIcon, GiftIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { Drawer } from '../shared/Drawer';
import { IntervalChoice } from '../shared/IntervalChoice';
import { dayShort } from '../../core/format/dates';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import { useSubscriptionBuilder, type BuilderTab } from './useSubscriptionBuilder';
const tabs: {
  key: BuilderTab;
  label: string;
}[] = [{
  key: 'protocol',
  label: 'Een protocol'
}, {
  key: 'products',
  label: 'Losse producten'
}];
const stepTitle = 'text-[13px] text-muted';
const chip = (on: boolean) => `whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] transition-[background-color,color,box-shadow] duration-150 ease-soft ${on ? 'bg-ink text-shell' : 'text-ink ring-1 ring-line hover:ring-ink/40'}`;

/**
 * "Stel je abonnement samen": een lade met drie stappen in één scherm. Wat je bijvult, hoe vaak,
 * en hoe de eerste leveringen eruitzien. De knop legt alles als bijvullen in de mand, met het
 * gekozen ritme; afrekenen gaat daarna gewoon via de mand.
 */
export function SubscriptionBuilder() {
  const b = useSubscriptionBuilder();
  const {
    routineTotals,
    productById
  } = useCatalog();
  // Tabbladen volgens de ARIA-afspraak: pijltjes wisselen, Tab gaat door naar de inhoud.
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const at = tabs.findIndex(t => t.key === b.tab);
    const nextIndex = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (at + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const next = tabs[nextIndex];
    if (!next) return;
    b.setTab(next.key);
    document.getElementById(`builder-tab-${next.key}`)?.focus();
  };
  const nameOf = (id: string) => productById(id)?.name ?? id;
  const footer = <div data-id="element-9123" data-mp="vr72iv35fq98">
      {(b.empty || b.tooSmall) && <p id="builder-empty" className="mb-3 text-center text-[13px] text-muted" data-id="element-9124" data-mp="19jdfk8b09h4g">
          {b.empty ? 'Kies eerst wat je wilt bijvullen.' : `Een doos is minstens ${euro(b.minBoxValue)}: voeg nog iets toe.`}
        </p>}
      <button type="button" onClick={b.submit} disabled={b.empty || b.tooSmall} aria-describedby={b.empty || b.tooSmall ? 'builder-empty' : undefined} className={btnPrice} data-id="element-9125" data-mp="hsh5tgk8rsle">
        <span className={btnPriceLabel} data-id="element-9126" data-mp="qtnp1xe9zhic">In de mand</span>
        <span className={btnPriceAmount} data-id="element-9127" data-mp="lszxnam4wynl">
          {euro(b.firstOrder)}
          <span className="text-[12px] opacity-80" data-id="element-9128" data-mp="227j5raktvybo"> nu</span>
        </span>
      </button>
      <p className="mt-3 text-center text-[13px] text-muted" data-id="element-9129" data-mp="229c8wfw8g0ex">Zelfde prijs als los · {policyCopy.subscriptionPerks}</p>
    </div>;
  return <Drawer open={b.open} onClose={b.close} title="Stel je abonnement samen" footer={footer} data-id="element-9130" data-mp="25odtkbya6j5r">
      <section aria-labelledby="builder-what" data-id="element-9131" data-mp="29rafabj4avdx">
        <h3 id="builder-what" className={stepTitle} data-id="element-9132" data-mp="29oxtfna8o0vl">
          1 · Wat vul ik bij
        </h3>
        <div role="tablist" aria-label="Wat vul ik bij" className="mt-3 flex gap-1.5" onKeyDown={onTabKey} data-id="element-9133" data-mp="1vtdknko84z1w">
          {tabs.map(t => <button key={t.key} type="button" role="tab" id={`builder-tab-${t.key}`} aria-selected={b.tab === t.key} aria-controls={`builder-panel-${t.key}`} tabIndex={b.tab === t.key ? 0 : -1} onClick={() => b.setTab(t.key)} className={chip(b.tab === t.key)} data-id="element-9134" data-mp="2ofesizzeiepb">
              {t.label}
            </button>)}
        </div>

        {b.tab === 'protocol' ? <ul id="builder-panel-protocol" role="tabpanel" aria-labelledby="builder-tab-protocol" className="mt-4 divide-y divide-line border-y border-line" data-id="element-9135" data-mp="3c5skyrlwefkf">
            {b.routineOptions.map(r => {
          const {
            items,
            price,
            months
          } = routineTotals(r);
          const on = b.routineId === r.id;
          return <li key={r.id} data-id="element-9136" data-mp="1ej9wqh5fgl8a">
                  <button type="button"
            // Eén protocol tegelijk: een keuze, geen vinkje (schermlezer hoort "keuzerondje").
            role="radio" aria-checked={on} onClick={() => b.toggleRoutine(r.id)} className="flex w-full items-center gap-3 py-3 text-left" data-id="element-9137" data-mp="19jx054or7zsi">
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${on ? 'bg-ink text-shell' : 'ring-1 ring-line'}`} aria-hidden="true" data-id="element-9138" data-mp="1xfj9n14lf5qp">
                      {on && <CheckIcon className="h-3 w-3" strokeWidth={2.5} data-id="element-9139" data-mp="2q09mp64aynz5" />}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-9140" data-mp="2l5di76hvq855">
                      <span className="block text-[15px] leading-snug text-ink" data-id="element-9141" data-mp="2sjefzaftbtyi">{r.name}</span>
                      <span className="block text-[12px] text-muted" data-id="element-9142" data-mp="2xgwee6w7eqs9">
                        {items.length} producten · gaat {months} mee
                      </span>
                    </span>
                    <span className="shrink-0 text-[14px] tabular-nums text-ink" data-id="element-9143" data-mp="37geycnc46ckb">{euro(price)}</span>
                  </button>
                </li>;
        })}
          </ul> : <div id="builder-panel-products" role="tabpanel" aria-labelledby="builder-tab-products" className="mt-4" data-id="element-9144" data-mp="3gfs7fewbsz4i">
            {b.chips.length > 1 && <div className="-mx-6 flex gap-1.5 overflow-x-auto px-6 pb-1" role="group" aria-label="Filter op huid" data-id="element-9145" data-mp="3l9d5f6xx5t3p">
                <button type="button" aria-pressed={b.skin === null} onClick={() => b.setSkin(null)} className={chip(b.skin === null)} data-id="element-9146" data-mp="3v5g608d4hjoj">
                  Alles
                </button>
                {b.chips.map(s => <button key={s} type="button" aria-pressed={b.skin === s} onClick={() => b.setSkin(b.skin === s ? null : s)} className={chip(b.skin === s)} data-id="element-9147" data-mp="201i9r6jlsbt3">
                    {s}
                  </button>)}
              </div>}
            <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-9148" data-mp="28wg66nlym6p6">
              {b.productList.map(p => {
            const n = b.qty[p.id] ?? 0;
            return <li key={p.id} className="flex items-center gap-3 py-2.5" data-id="element-9149" data-mp="2iuvt0edyetsv">
                    <ProductImage src={p.image} alt="" width={40} className="h-12 w-10 shrink-0 bg-sand p-1" data-id="element-9150" data-mp="2dt6gsvezwa37" />
                    <div className="min-w-0 flex-1" data-id="element-9151" data-mp="2rso9vmzbq9id">
                      {b.planIds.has(p.id) && <p className="text-[11px] text-clay" data-id="element-9152" data-mp="2nt72up4914b3">Uit je huidplan</p>}
                      <p className="truncate text-[14px] leading-snug text-ink" data-id="element-9153" data-mp="xeojgkhr406g">{p.name}</p>
                      <p className="text-[12px] text-muted" data-id="element-9154" data-mp="temosvafo70o">
                        {euro(p.price)}
                        {p.usesPerBottle && ` · ${p.usesPerBottle}`}
                      </p>
                    </div>
                    {n === 0 ? <button type="button" onClick={() => b.setProductQty(p.id, 1)} aria-label={`${p.name} toevoegen`} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink ring-1 ring-line transition-shadow duration-150 ease-soft hover:ring-ink/40" data-id="element-9155" data-mp="ndm8b60er4d9">
                        <PlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-9156" data-mp="4inmpmdi3z52" />
                      </button> : <div className="flex shrink-0 items-center rounded-full bg-ink text-shell" data-id="element-9157" data-mp="3rqrch36tgqsl">
                        <button type="button" onClick={() => b.setProductQty(p.id, n - 1)} aria-label={`Eén minder: ${p.name}`} className="flex h-10 w-9 items-center justify-center" data-id="element-9158" data-mp="1l0bdfoqepx3c">
                          <MinusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-9159" data-mp="2yjbnswz4td52" />
                        </button>
                        <span className="min-w-[1rem] text-center text-[13px] tabular-nums" aria-live="polite" data-id="element-9160" data-mp="2tk8uuawfexdg">
                          {n}
                        </span>
                        <button type="button" onClick={() => b.setProductQty(p.id, n + 1)} aria-label={`Eén meer: ${p.name}`} className="flex h-10 w-9 items-center justify-center" data-id="element-9161" data-mp="2ks6vkfy3fn4r">
                          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-9162" data-mp="2athc4iqjtume" />
                        </button>
                      </div>}
                  </li>;
          })}
            </ul>
          </div>}
        {b.count > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-9163" data-mp="42i081zcj2pz">
            Gekozen: {b.count} {b.count === 1 ? 'product' : 'producten'}
            {b.tab === 'products' && b.routineId && ' (met je protocol)'}
            {b.tab === 'protocol' && Object.values(b.qty).some(n => n > 0) && ' (met je losse producten)'}
          </p>}
      </section>

      <section aria-labelledby="builder-how" className="mt-9" data-id="element-9164" data-mp="1mwb8bfdzcyiu">
        <h3 id="builder-how" className={stepTitle} data-id="element-9165" data-mp="1rocld3iz2t9x">
          2 · Hoe vaak
        </h3>
        {b.intervals.length > 1 && <div className="mt-3" data-id="element-9166" data-mp="1vpodpgt8dslt">
            <IntervalChoice intervals={b.intervals} value={b.intervalWeeks} onChange={b.chooseInterval} labelledBy="builder-how" describedBy="builder-how-hint" data-id="element-9167" data-mp="1vs0rrivvbitl" />
          </div>}
        <p id="builder-how-hint" className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-9168" data-mp="1414ggmpghm8f">
          {b.empty ? 'Kies eerst wat je bijvult; dan reken ik het uit.' : b.tooSmall ? `Een doos is altijd minstens ${euro(b.minBoxValue)}, zodat verzending gratis blijft. Met alleen dit lukt dat niet: voeg nog een product toe.` : `Elke ${b.intervalWeeks} weken een doos met alleen wat bijna op is. Niet steeds alles: wat langer meegaat, komt minder vaak.`}{' '}
          Je krijgt vooraf een mail; overslaan of pauzeren kan altijd in Mijn omgeving.
        </p>
      </section>

      <section aria-labelledby="builder-preview" className="mt-9" data-id="element-9169" data-mp="rfvfs0kio1n4">
        <h3 id="builder-preview" className={stepTitle} data-id="element-9170" data-mp="rgncxftri7fi">
          3 · Zo ziet het eruit
        </h3>
        {b.boxes.length > 0 ? <ol className="mt-3 divide-y divide-line border-y border-line" data-id="element-9171" data-mp="3uwu5oran2k9i">
            <li className="flex items-baseline justify-between gap-3 py-3 text-[14px]" data-id="element-9172" data-mp="3uyovaiwuv0jx">
              <span className="text-ink" data-id="element-9173" data-mp="3qjb9e04whr5">Nu · je eerste bestelling, alles</span>
              <span className="tabular-nums text-muted" data-id="element-9174" data-mp="8okqtoycpyxv">{euro(b.firstOrder)}</span>
            </li>
            {b.boxes.map((box, i) => <li key={box.date} className="py-3" data-id="element-9175" data-mp="comoln0yu87v" data-mpi={i}>
                <p className="flex items-baseline justify-between gap-3 text-[14px]" data-id="element-9176" data-mp="1k3rbb1ycnnib">
                  <span className="text-ink" data-id="element-9177" data-mp="1o5357jo9auwb">
                    {dayShort(box.date)}
                    {b.giftAt === i + 1 && <span className="ml-2 inline-flex items-center gap-1 text-[12px] text-clay" data-id="element-9178" data-mp="38ndl93egg4ts">
                        <GiftIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-9179" data-mp="34nd7lkz6jxns" /> met cadeau
                      </span>}
                  </span>
                  <span className="tabular-nums text-muted" data-id="element-9180" data-mp="2uvz4z4bxmhqf">{euro(box.total)}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-muted" data-id="element-9181" data-mp="2pz8pai41ho5j">
                  {box.lines.map(l => `${l.qty > 1 ? `${l.qty}× ` : ''}${nameOf(l.productId)}`).join(', ')}
                </p>
              </li>)}
          </ol> : <p className="mt-3 text-[13px] text-muted" data-id="element-9182" data-mp="2by5zbu9xbtpc">{b.empty ? 'Nog niets gekozen.' : 'Voeg nog iets toe, dan zie je hier je dozen.'}</p>}
        {b.range &&
      // Wijzigt bij elke keuze in stap 1 en 2: voorlezen, zonder de focus te verplaatsen.
      <p className="mt-2 text-[13px] text-muted" aria-live="polite" data-id="element-9183" data-mp="a4ghx7kyw3nm">
            {b.range.min === b.range.max ? `${euro(b.range.min)} per doos` : `Meestal ${euro(b.range.min)} tot ${euro(b.range.max)} per doos`}, altijd gratis verzending.
          </p>}
      </section>
    </Drawer>;
}